import { useCallback, useEffect, useRef, useState } from "react";
import { Column } from "../../../../core/components/Column";
import { CustomButton } from "../../../../core/components/CustomButton";
import CircularProgress from "../../../../core/components/CircularProgress";
import { TextComponent } from "../../../../core/components/TextComponent";
import {
	incomingLikeEmailPreferenceApi,
	LikeEmailFrequency,
} from "../../data/likeEmailPreferenceApi";
import { LikeEmailFrequencySelector } from "./LikeEmailFrequencySelector";

export const SignedInLikeEmailPreference = () => {
	const [frequency, setFrequency] = useState<LikeEmailFrequency>("daily");
	const [loading, setLoading] = useState(true);
	const [loaded, setLoaded] = useState(false);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState("");
	const [error, setError] = useState("");
	const requestGeneration = useRef(0);

	const loadPreference = useCallback(async () => {
		const generation = ++requestGeneration.current;
		setLoading(true);
		setLoaded(false);
		setMessage("");
		setError("");
		try {
			const savedFrequency = await incomingLikeEmailPreferenceApi.getSignedIn();
			if (generation !== requestGeneration.current) return;
			setFrequency(savedFrequency);
			setLoaded(true);
		} catch {
			if (generation !== requestGeneration.current) return;
			setError("We could not load your Likes You email preference.");
		} finally {
			if (generation === requestGeneration.current) setLoading(false);
		}
	}, []);

	useEffect(() => {
		void loadPreference();
		return () => {
			requestGeneration.current += 1;
		};
	}, [loadPreference]);

	const savePreference = async (nextFrequency: LikeEmailFrequency) => {
		if (!loaded || saving) return;
		const previousFrequency = frequency;
		const generation = ++requestGeneration.current;
		setFrequency(nextFrequency);
		setSaving(true);
		setMessage("");
		setError("");
		try {
			const savedFrequency = await incomingLikeEmailPreferenceApi.saveSignedIn(nextFrequency);
			if (generation !== requestGeneration.current) return;
			setFrequency(savedFrequency);
			setMessage("Likes You email preference saved.");
		} catch {
			if (generation !== requestGeneration.current) return;
			setFrequency(previousFrequency);
			setError("We could not save your preference. Please try again.");
		} finally {
			if (generation === requestGeneration.current) setSaving(false);
		}
	};

	return (
		<Column className="mt-6 gap-4 rounded-xl border border-grayLight p-5">
			<TextComponent variant="h4" className="font-semibold text-primary">
				Likes You email reminders
			</TextComponent>
			<TextComponent variant="h6">
				Choose how often we send a bundled email reminder about genuine new people who still like you.
			</TextComponent>
			<TextComponent variant="h6" className="text-graySecondary">
				Immediate app alerts are separate and still follow your Likes notification setting above.
			</TextComponent>

			{loading ? (
				<div className="flex justify-center py-5" aria-label="Loading Likes You email preference">
					<CircularProgress size={40} />
				</div>
			) : loaded ? (
				<LikeEmailFrequencySelector
					value={frequency}
					disabled={saving}
					onChange={savePreference}
				/>
			) : (
				<CustomButton type="button" onClick={loadPreference}>
					Try again
				</CustomButton>
			)}

			{saving && (
				<TextComponent variant="h6" className="text-graySecondary" role="status">
					Saving your preference…
				</TextComponent>
			)}
			{message && (
				<TextComponent variant="h6" className="font-semibold text-green-700" role="status">
					{message}
				</TextComponent>
			)}
			{error && (
				<TextComponent variant="h6" className="font-semibold text-red-600" role="alert">
					{error}
				</TextComponent>
			)}
		</Column>
	);
};
